/**
 * Partner Launchpad - landing page for downstream partner users.
 *
 * Shows the partner's own organisation info and cards for each upstream
 * tenant they can "switch into" via partnership access.
 */

import { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { useQueryClient } from '@tanstack/react-query'
import { Building2, ArrowRight, Handshake, LogOut, ChevronDown } from 'lucide-react'
import { setLoggingOut } from '@/admin/lib/api'
import { headlessLogout } from '@/shared/lib/auth-api'
import { clearPersistedCacheForCurrentPartition } from '@/admin/lib/query-persistence/setup'
import { useOrg } from '@/admin/hooks/use-org'
import type { PartnershipContext } from '@/admin/hooks/use-org'
import { Button } from '@/shared/components/ui/button'
import { Logo } from '@/shared/components/ui/logo'
import { buildTenantUrl, getTenantSlugFromHost } from '@/shared/lib/tenant-host'

export function PartnerLaunchpadPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { data, partnerships, switchToPartner, isLoading } = useOrg()
  const [menuOpen, setMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  // Close menu on outside click
  useEffect(() => {
    if (!menuOpen) return
    function handleClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [menuOpen])

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--bg)]">
        <p className="text-[13px] text-[var(--ink-3)]">Loading...</p>
      </div>
    );
  }

  const user = data?.user
  const ownOrgs = data?.available_organisations ?? []
  const ownOrgName = ownOrgs[0]?.name ?? 'Your Organisation'

  function handleEnterPartner(p: PartnershipContext) {
    // Each tenant lives at its own `<slug>.domain`, so open a cross-tenant
    // partner in its own tab rather than swapping headers in place - the user
    // can keep their own workspace open alongside it. Falls back to the
    // in-place switch when the partner is in the current tenant or the host is
    // generic (no subdomain to build from).
    const slug = p.upstream_organisation.tenant_slug
    if (slug && slug !== getTenantSlugFromHost()) {
      const url = buildTenantUrl(slug, '/dashboard')
      if (url) {
        window.open(url, '_blank', 'noopener')
        return
      }
    }
    switchToPartner(p.upstream_organisation.uuid, p.downstream_organisation.uuid)
    navigate('/dashboard')
  }

  async function handleLogout() {
    setMenuOpen(false)
    try {
      setLoggingOut(true)
      await clearPersistedCacheForCurrentPartition()
      await headlessLogout()
      queryClient.clear()
      const slug = sessionStorage.getItem('tenantSlug')
      sessionStorage.removeItem('tenantSlug')
      sessionStorage.removeItem('activeOrganisationUuid')
      sessionStorage.removeItem('activePartnerUuid')
      navigate(slug ? `/${slug}/login` : '/login')
    } catch {
      // Force redirect even if the API call fails
      window.location.href = '/login'
    } finally {
      setLoggingOut(false)
    }
  }

  return (
    <div className="min-h-screen bg-[var(--bg)]">
      {/* Header */}
      <header
        className="flex h-14 items-center justify-between border-b-[0.5px] border-[var(--rule)] px-6"
      >
        <div className="flex items-center gap-3">
          <Logo size="sm" />
        </div>

        {/* User menu */}
        <div className="relative" ref={menuRef}>
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="flex items-center gap-2 cursor-pointer rounded-md px-2 py-1.5 transition-colors hover:bg-[var(--bg-subtle)]"
          >
            <span className="text-[12px] text-[var(--ink-3)]">
              {user?.first_name} {user?.last_name}
            </span>
            <ChevronDown className="h-3 w-3 text-[var(--ink-3)]" />
          </button>

          {menuOpen && (
            <div className="absolute right-0 top-full mt-1 z-50 min-w-[200px] rounded-[8px] border-[0.5px] border-[var(--rule-strong)] bg-[var(--bg)] shadow-[var(--shadow-float)]">
              <div className="px-3 py-2.5 border-b-[0.5px] border-b-[var(--rule)]">
                <p className="text-[12px] text-[var(--ink)]">
                  {user?.first_name} {user?.last_name}
                </p>
                <p className="text-[10px] text-[var(--ink-3)]">
                  {user?.email}
                </p>
              </div>
              <div className="py-0.5">
                <Button
                  type="button"
                  onClick={handleLogout}
                  variant="ghost"
                  size="xs"
                  className="h-auto w-full cursor-pointer justify-start rounded-none px-3 py-2 text-left text-[13px] font-[350] text-[var(--ink-2)] hover:bg-[var(--bg-subtle)] hover:text-[var(--ink-2)]"
                >
                  <LogOut className="h-3.5 w-3.5 shrink-0 text-[var(--ink-3)]" />
                  Sign out
                </Button>
              </div>
            </div>
          )}
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-6 py-16">
        {/* Welcome */}
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)]">
              <Building2 className="h-6 w-6 text-[var(--ink-3)]" />
            </div>
            <div>
              <h1 className="clara-title">{ownOrgName}</h1>
              <p className="text-[13px] text-[var(--ink-3)]">
                Partner Portal
              </p>
            </div>
          </div>
        </div>

        {/* Partnership cards */}
        <div className="mb-6">
          <h2
            className="clara-section-label mb-6 border-b-[0.5px] border-[var(--rule)] pb-3"
          >
            <Handshake className="mr-2 -mt-0.5 inline-block h-3.5 w-3.5 text-[var(--ink-3)]" />
            Your Partnerships
          </h2>

          {partnerships.length === 0 ? (
            <p className="py-8 text-center text-[13px] text-[var(--ink-3)]">
              No active partnerships. Contact your partner to get started.
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {partnerships.map((p) => (
                <button
                  key={`${p.upstream_organisation.uuid}-${p.downstream_organisation.uuid}`}
                  onClick={() => handleEnterPartner(p)}
                  className="group cursor-pointer rounded-[12px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] p-6 text-left transition-all duration-200 hover:border-[var(--green)] hover:shadow-[0_2px_12px_var(--green-bg-muted)]"
                >
                  {/* Upstream org identity */}
                  <div className="flex items-start justify-between mb-6">
                    {p.upstream_organisation.logo_url ? (
                      <img
                        src={p.upstream_organisation.logo_url}
                        alt={p.upstream_organisation.name}
                        className="h-14 max-w-[140px] object-contain"
                      />
                    ) : (
                      <div className="flex h-14 w-14 items-center justify-center rounded-xl border-[0.5px] border-[var(--green-border)] bg-[var(--green-bg)] text-[18px] text-[var(--green)]">
                        {p.upstream_organisation.name.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <ArrowRight
                      className="mt-1 h-4 w-4 text-[var(--ink-3)] transition-transform duration-200 group-hover:translate-x-1"
                    />
                  </div>

                  {/* The record they enter AS, not the network they enter
                      INTO. A person with access to two dealers under one tenant
                      saw the tenant's name on both cards and had no way to tell
                      them apart. The network stays as context below. */}
                  <h3 className="mb-1 text-[18px] text-[var(--ink)]">
                    {p.downstream_organisation.name}
                  </h3>
                  <p className="text-[12px] text-[var(--ink-3)]">
                    {t('partnerships.launchpadNetwork', 'in {{network}}', {
                      network: p.upstream_organisation.name,
                    })}
                  </p>

                  <div className="flex items-center gap-2 mt-3">
                    {p.role_name && (
                      <span className="rounded-full bg-[var(--bg-subtle)] px-2 py-0.5 text-[9px] uppercase tracking-[0.1em] text-[var(--ink-3)]">
                        {p.role_name}
                      </span>
                    )}
                    <span className="rounded-full border-[0.5px] border-[var(--green-border)] bg-[var(--green-bg)] px-2 py-0.5 text-[9px] uppercase tracking-[0.1em] text-[var(--green)]">
                      Active
                    </span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
